import { Box, Button, CircularProgress, styled } from '@mui/material';
import React, { createRef, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useCart from '../../hooks/cart/useCart';
import SnackbarUtils from '../../utils/SnackbarUtils';
import { Spinner } from './Spinner';
import useCartUpdate from '../../hooks/cart/useCartUpdate';
import ShoppingCartOutlined from '@mui/icons-material/ShoppingCartOutlined';
import styledComponent from 'styled-components';

const AddToIconButton = styled(Button)(({ theme }) => ({
    width: '100%',
    textTransform: 'uppercase',
    minWidth: 0,
    minHeight: 0,
    fontWeight: 600,
    background: `${theme.palette.primary[400]}`,
    border: `1px solid ${theme.palette.primary[400]}`,
    color: 'white',
    height: '40px',
    ':hover': {
        background: theme.custom.primaryBtnColor,
        border: '1px solid black',
        color: 'black',
    },
}));

const StyledDiv = styledComponent.div`
    flex: 1;
    overflow: hidden;
    height: 24px;
    width: 24px;
`;

export const ProductAddToCartButton: React.FC<{
    variant: number;
    quantity: number;
    availableQty: number;
}> = (props) => {
    const { variant, quantity, availableQty } = props;
    const { t } = useTranslation();
    const cart = useCart();
    const cartUpdate = useCartUpdate();
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [isSmall, setSmall] = useState<boolean>(false);

    const containerRef = createRef<HTMLDivElement>();

    const addToCart = () => {
        setIsLoading(true);
        cart.addProductsToCart([
            cart.convertCartLineItems({
                shopify_variant_id: variant,
                quantity: quantity,
            }),
        ])
            .then(() => {
                SnackbarUtils.success(t('NOTIFICATION.ADDED_TO_CART'));
                cartUpdate.updateCart();
            })
            .catch(() => {
                SnackbarUtils.error(
                    t('NOTIFICATION.ADDED_TO_CART_UNSUCCESSFUL')
                );
            })
            .finally(() => setIsLoading(false));
    };

    // this useMemo hook calculates the content of the button based on its width
    const buttonContent = useMemo((): React.ReactNode => {
        if (isSmall) return <ShoppingCartOutlined />;
        return availableQty > 0
            ? t('ADD_TO_CART_BUTTON.ADD_TO')
            : t('ADD_TO_CART_BUTTON.PRE_ORDER');
    }, [isSmall, availableQty]);

    // this useEffect hook calculates if the button isSmall or not | width is less than 120px
    useEffect(() => {
        if (!containerRef.current) return;
        const resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                if (entry.contentRect.width < 120) {
                    setSmall(true);
                    return;
                }

                setSmall(false);
            }
        });
        resizeObserver.observe(containerRef.current);
        return () => resizeObserver.disconnect(); // clean up
    }, []);

    return (
        <AddToIconButton
            onClick={addToCart}
            sx={{ height: '100%', maxWidth: '250px' }}
        >
            <StyledDiv ref={containerRef}>
                {!isLoading ? buttonContent : <CircularProgress size={24} />}
            </StyledDiv>
        </AddToIconButton>
    );
};
